import {
  CREATE_BLOCK_FULFILLED,
  UPDATE_BLOCK_FULFILLED,
  UPDATE_MULTIPLE_BLOCKS_FULFILLED,
  DELETE_BLOCK_FULFILLED,
  FETCH_PLANNER_FULFILLED,
  UPSERT_NOTE_FULFILLED,
  DELETE_NOTE_FULFILLED,
  LOAD_MORE_WEEKS_AND_BLOCKS_FULFILLED,
  CREATE_PLAN_FULFILLED,
  BULK_DELETE_BLOCKS_FULFILLED,
} from './actionTypes';

const initialState = {
  // Plan metadata
  planId: null,
  calendarId: null,
  userId: null,

  // Blocks keyed by block id
  blocks: {},

  // Notes keyed by week identifier (e.g., '2025-W01')
  notes: {},
};

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case CREATE_PLAN_FULFILLED: {
      return {
        ...state,
        planId: action.payload.planId,
        calendarId: action.payload.calendarId,
        userId: action.payload.userId,
      };
    }

    case FETCH_PLANNER_FULFILLED: {
      // Filter out soft-deleted records
      const blocks = action.payload.blocks || {};
      const notes = action.payload.notes || {};

      const filteredBlocks = Object.entries(blocks).reduce((acc, [id, block]) => {
        if (!block.del) {
          acc[id] = block;
        }
        return acc;
      }, {});

      const filteredNotes = Object.entries(notes).reduce((acc, [week, note]) => {
        if (!note.del) {
          acc[week] = note;
        }
        return acc;
      }, {});

      return {
        ...state,
        planId: action.payload.planId || state.planId,
        calendarId: action.payload.calendarId,
        userId: action.payload.userId,
        blocks: filteredBlocks,
        notes: filteredNotes,
      };
    }

    case CREATE_BLOCK_FULFILLED: {
      const { block } = action.payload;
      return {
        ...state,
        blocks: {
          ...state.blocks,
          [block.id]: block,
        },
      };
    }

    case UPDATE_BLOCK_FULFILLED: {
      const { block, oldId } = action.payload;

      // If oldId is provided, we're replacing a temp ID with a real ID
      if (oldId && oldId !== block.id) {
        const { [oldId]: oldBlock, ...remainingBlocks } = state.blocks;
        return {
          ...state,
          blocks: {
            ...remainingBlocks,
            [block.id]: {
              ...oldBlock,
              ...block,
            },
          },
        };
      }

      // Normal update
      return {
        ...state,
        blocks: {
          ...state.blocks,
          [block.id]: {
            ...state.blocks[block.id],
            ...block,
          },
        },
      };
    }

    case DELETE_BLOCK_FULFILLED: {
      const { blockId } = action.payload;
      const { [blockId]: deletedBlock, ...remainingBlocks } = state.blocks;
      return {
        ...state,
        blocks: remainingBlocks,
      };
    }

    case UPSERT_NOTE_FULFILLED: {
      const { week, note } = action.payload;
      return {
        ...state,
        notes: {
          ...state.notes,
          [week]: note,
        },
      };
    }

    case DELETE_NOTE_FULFILLED: {
      const { week } = action.payload;
      const { [week]: deletedNote, ...remainingNotes } = state.notes;
      return {
        ...state,
        notes: remainingNotes,
      };
    }

    case LOAD_MORE_WEEKS_AND_BLOCKS_FULFILLED: {
      // Merge additional blocks and notes, filtering out soft-deleted records
      const blocksSrc = action.payload.blocks || {};
      const notesSrc = action.payload.notes || {};

      const filteredBlocks = Object.entries(blocksSrc).reduce((acc, [id, block]) => {
        if (!block.del) {
          acc[id] = block;
        }
        return acc;
      }, {});

      const filteredNotes = Object.entries(notesSrc).reduce((acc, [week, note]) => {
        if (!note.del) {
          acc[week] = note;
        }
        return acc;
      }, {});

      return {
        ...state,
        blocks: {
          ...state.blocks,
          ...filteredBlocks,
        },
        notes: {
          ...state.notes,
          ...filteredNotes,
        },
      };
    }

    case UPDATE_MULTIPLE_BLOCKS_FULFILLED: {
      // Batch update multiple blocks at once
      const { blocks, idMappings } = action.payload;

      // Start with current blocks
      const updatedBlocks = { ...state.blocks };

      // Handle temp ID to real ID replacements if mappings provided
      if (idMappings && typeof idMappings === 'object') {
        Object.entries(idMappings).forEach(([oldId, newId]) => {
          if (oldId !== newId && updatedBlocks[oldId]) {
            const oldBlock = updatedBlocks[oldId];
            delete updatedBlocks[oldId];
            updatedBlocks[newId] = { ...oldBlock, id: newId };
          }
        });
      }

      // Filter out soft-deleted blocks and merge incoming blocks
      const filteredBlocks = Object.entries(blocks || {}).reduce((acc, [id, block]) => {
        if (!block.del) {
          acc[id] = {
            ...updatedBlocks[id],
            ...block,
          };
        } else if (updatedBlocks[id]) {
          // Remove soft-deleted blocks from state
          delete updatedBlocks[id];
        }
        return acc;
      }, {});

      return {
        ...state,
        blocks: {
          ...updatedBlocks,
          ...filteredBlocks,
        },
      };
    }

    case BULK_DELETE_BLOCKS_FULFILLED: {
      // The backend handles all splitting/mutation logic and returns the full
      // updated plan state in the same shape as fetchPlan. Replace entirely.
      return {
        ...state,
        blocks: action.payload.blocks,
        notes: action.payload.notes,
      };
    }

    default:
      return state;
  }
}
